import css from "./storm.module.scss";
import { Layout } from "../../../componentsV1/layout";
import Logo from "../../../assets/v1/logo.png";

export const Storm = () => {
  const workflows = [
    {
      title: "Configure Calibration Workflow",
      description: 'The SRX Calibration Workflow begins with system configuration parameters optimized for dual-camera localization microscopy alignment.'
    },
     {
      title: "Select Alignment Conditions",
      description: 'The bead sample is focused in Widefield View Mode, then evaluated under SML View Mode using defined 640 nm widefield and SuperRes imaging conditions.'
    },
     {
      title: "Record Alignment Dataset",
      description: 'Alignment recordings are captured using 30 frames, one Z position, 0.1 μm step settings, and laser power controlled from the selected preset.'
    },
     {
      title: "Align Bi-Plane imaigng Planes",
      description: 'The reference plane is selected and plane 2 is auto-aligned against the reference. SRX software reports alignment quality as Goof, Fair, or Poor.'
    },
     {
      title: "Select PSF Beads",
      description: 'The field of view is examined for 4-10 non overlapping, well-separated 100 nm TetraSpeck beads to support accurate PSF reconstruction.'
    },
     {
      title: "Record PSF Dataset",
      description: 'PSF recordings use 30 frames, one Z position, 0.1 μm step settings, 20 ms exposure, and preset laser power conditions.'
    },
     {
      title: "Isolate PSF Beads",
      description: 'At least four spatially separated beads are isolated tro maximize measurement quality while reducing interference from nearby signals.'
    },
     {
      title: "Save PSF Measurement",
      description: 'PSF measurements are generated and scored by SRX software. Identifyn® targets Good PSF classifications and does not accept Poor classifications.'
    }
  ]

  return (
    <Layout>
      <div className={css.heroContainer}>
        <div className={css.heroContent}>
          <img src={Logo} alt="logo" className={css.heroLogo} />
          <div className={css.heroText}>
            <h1>Precision Calibration for Single-Molecule Localization Microscopy</h1>
            <div className={css.line} />
            <p>Identifyn® STORM calibration workflows establish reproducible nanoscale imaging performance across the Bruker™ Vutara™ VXL dual-camera bi-plane imaging platform.</p>
          </div>
        </div>
      </div>
      <div className={css.container}>
        {/* first section */}
        <div className={css.column}>
          <h5 className={css.specialText}>Calibration Philosophy</h5>
          <div className={css.row}>
            <h2 className={css.heading}>Calibration is the foundation of localization precision.</h2>
            <div className={css.column}>
              <p>Single-molecule localization microscopy depends on accurate optical alignment and stable PSF reconstruction to generate biologically meaningful nanoscale imaging data.</p>
              <p>Identifyn® calibration workflows optimize dual-camera alignment, bi-plane registration, PSF quality, signal-to-noise performance, and reconstruction fidelity before STORM acquisition begins.</p>
            </div>
          </div>
        </div>
        {/* second section */}
        <div className={css.card}>
          <div className={css.row}>
            <div className={`${css.column} ${css.flex}`}>
              <h5 className={css.specialText}>Calibration Platform</h5>
              <h2 className={css.heading}>Bruker™ Vutara™ VXL Dual-Camera Bi-Plane System</h2>
              <p>Calibration is performed using 100 nm TetraSpeck™ beads in PBS within an 8-well Ibidi chamber to support controled alignment and PSF measurement workflows.</p>
            </div>
            <div className={`${css.twoColumn} ${css.flex}`}>
              <div className={css.pillCard}>
                <p>Bruker™ Vutara™ VXL Platform</p>
              </div>
              <div className={css.pillCard}>
                <p>Dual Camera Acquisition</p>
              </div>
              <div className={css.pillCard}>
                <p>Bi-Plane Imaging Configuration</p>
              </div>
              <div className={css.pillCard}>
                <p>100 nm TetraSpeck™ Beads</p>
              </div>
              <div className={css.pillCard}>
                <p>8-well Ibidi Chamber</p>
              </div>
              <div className={css.pillCard}>
                <p>PBS Calibration Conditions</p>
              </div>
              <div className={css.pillCard}>
                <p>SRX Software Workflow</p>
              </div>
              <div className={css.pillCard}>
                <p>Sequential Probe Acquisition</p>
              </div>
            </div>
          </div>
        </div>
        {/* third section */}
        <div className={css.column}>
          <h5 className={css.specialText}>SRX Configuration</h5>
          <h2 className={css.heading}>Standardized calibration parameters for reproducible STORM performance.</h2>
          <div className={css.autoGrid}>
            <div className={`${css.singleColumn} ${css.card} ${css.fullHeight}`}>
              <p>Capture Configuration</p>
              <div className={css.divider} />
              <p className={css.smallText}>Number of probes: 1</p>
              <div className={css.divider} />
              <p className={css.smallText}>Number of simultaneous probes: 1</p>
              <div className={css.divider} />
              <p className={css.smallText}>Camera: both cameras</p>
              <div className={css.divider} />
              <p className={css.smallText}>Channel: both channels</p> 
              <div className={css.divider} />
              <p className={css.smallText}>Probe capture mode: sequential</p>
              <div className={css.divider} />
              <p className={css.smallText}>Z-stack capture mode: sequential</p>
              <div className={css.divider} />
              <p className={css.smallText}>Autofocus drift correction: ON</p>
            </div>
            <div className={`${css.singleColumn} ${css.card} ${css.fullHeight}`}>
              <p>Camera Configuration</p>
              <div className={css.divider} />
              <p className={css.smallText}>Recording vertical field of view: 100%</p>
              <div className={css.divider} />
              <p className={css.smallText}>Widefield exposure time: 100 ms</p>
              <div className={css.divider} />
              <p className={css.smallText}>Super-resolution exposure time: 20 ms</p>
              <div className={css.divider} />
              <p className={css.smallText}>Widefield camera binning: 1</p>
              <div className={css.divider} />
              <p className={css.smallText}>Per-probe exposure enabled</p>
            </div>
            <div className={`${css.singleColumn} ${css.card} ${css.fullHeight}`}>
              <p>Microscope Configuration</p>
              <div className={css.divider} />
              <p className={css.smallText}>Bi-plane module: 635 nm dichroic</p>
              <div className={css.divider} />
              <p className={css.smallText}>Objective: silicon</p>
              <div className={css.divider} />
              <p className={css.smallText}>Stage Insert: rectangular</p>
              <div className={css.divider} />
              <p className={css.smallText}>Focus configuration: 8-well chamber</p>
              <div className={css.divider} />
              <p className={css.smallText}>Dichroic: SuperRes</p>
            </div>
            <div className={`${css.singleColumn} ${css.card} ${css.fullHeight}`}>
              <p>Probe Configuration</p>
              <div className={css.divider} />
              <p className={css.smallText}>Predefined probe: Calibration Probe 640 nm</p>
              <div className={css.divider} />
              <p className={css.smallText}>635 nm dichroic configuration</p>
              <div className={css.divider} />
              <p className={css.smallText}>Photoactivation / imaging laser: 0%</p>
              <div className={css.divider} />
              <p className={css.smallText}>Probe exposure time: 20 ms</p>
            </div>
          </div>
        </div>
        {/* fourth section */}
        <div className={css.column}>
        <div className={`${css.column} ${css.centerText}`}>
        <h5 className={css.specialText}>Calibration Workflow</h5>
        <h2 className={css.heading}>Alignment, PSF isolation, and quality classification in one controlled sequence.</h2>
        <p>The Identifyn® STORM calibration method follows a structured SRX workflow to establish optical alignment quality before nanoscale reconstruction.</p>
        </div>
        <div className={css.twoColumn}>
          {
          workflows.map((workflow, index) => (
            <div className={`${css.row} ${css.card}`} key={index}>
              <div className={css.numberLabel}>
                {(index + 1).toString().padStart(2, '0')}
              </div>
              <div className={css.singleColumn}>
                <p>{workflow.title}</p>
                <div className={css.divider} />
                <p>{workflow.description}</p>
              </div>
            </div>
          ))
          }
        </div>
        </div>
        {/* fifth section */}
        <div className={css.column}>
        <div className={`${css.column} ${css.centerText}`}>
        <h5 className={css.specialText}>Quality Gates</h5>
        <h2 className={css.heading}>Good whenever possible. Poor never accepted.</h2>
        </div>
        <div className={css.threeColumn}>
          <div className={`${css.singleColumn} ${css.card}`}>
            <p>Good</p>
            <div className={css.divider} />
            <p>Target classification for alignment and PSF quality whenever achievable.</p>
          </div>
           <div className={`${css.singleColumn} ${css.card}`}>
            <p>Fair</p>
            <div className={css.divider} />
            <p>Acceptable under defined wavelength dye, or chemistry-dependent conditions.</p>
          </div>
           <div className={`${css.singleColumn} ${css.card}`}>
            <p>Poor</p>
            <div className={css.divider} />
            <p>Rejected under Identifyn® calibration standards and not accepted for STORM validation.</p>
          </div>
        </div>
        </div>
        {/* sixth section */}
        <div className={css.row}>
          <div className={css.column}>
            <h5 className={css.specialText}>Representative PSF Measurement</h5>
            <h2 className={css.heading}>Example PSF values for Identifyn® SRM Alexa Fluor™ 647. </h2>
            <p>PSF values are variable for each calibration and depend on bead preparation, dye selection, optical configuration, and intended imaging conditions. The representative sample produced a Good PSF classification in SRX software.</p>
          </div>
          <div className={`${css.column} ${css.card}`}>
            <div className={css.tableThreeColumn}>
              <p>Parameter</p>
              <p>Plane 1</p>
              <p>Plane 2</p>
            </div>
            <div className={css.divider} />
            <div className={css.tableThreeColumn}>
              <p>ZFWHM</p>
              <p>816 nm</p>
              <p>806 nm</p>
            </div>
            <div className={css.divider} />
            <div className={css.tableThreeColumn}>
              <p>Tilt</p>
              <p>0.5 px</p>
              <p>0.9 px</p>
            </div>
            <div className={css.divider} />
            <div className={css.tableThreeColumn}>
              <p>Signal-to-noise ratio</p>
              <p>553</p>
              <p>731</p>
            </div>
            <div className={css.pillCard}>
            <p>SRX software classification: Good PSF measurement</p>
          </div>
          </div>
        </div>
        {/* seventh section */}
        <div className={`${css.column} ${css.centerText} ${css.smallContainer} ${css.card}`}>
          <h5 className={css.specialText}>STORM Calibration Standard</h5>
          <h2 className={css.heading}>Localization precision begins before the first molecule is reconstructed.</h2>
          <p>Identifyn® STORM calibration establishes the optical alignment, PSF quality, and quality-control foundation required to generate high-confidence single-molecule localization microscopy data under real experimental imaging conditions.</p>
          <div className={css.pillCard}>
            <p className={css.smallText}>
              Due to variability in microscopy systems, optical configurations, fluorophores, and experimental applications, this workflow represents Identifyn® internal Standard Operating Procedure guidance and may serve as a generalized reference framework for related single-molecule localization microscopy users.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}